import React, { useState } from 'react';
import { ShieldAlert, Download, Search, Calendar, User, FileText, Activity } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useAuditLogsDateRange, exportToCSV } from '@/hooks/use-api';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';

export function AuditLogsPage() {
  const [filter, setFilter] = useState('');
  const [dateRange, setDateRange] = useState();
  
  const startDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined;
  const endDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined;
  
  const { data, isLoading } = useAuditLogsDateRange(startDate, endDate);
  
  const filteredLogs = (data?.items || []).filter(log =>
    log.actorName.toLowerCase().includes(filter.toLowerCase()) ||
    log.action.toLowerCase().includes(filter.toLowerCase()) ||
    log.target.toLowerCase().includes(filter.toLowerCase())
  );
  const handleExport = () => {
    if (!filteredLogs || filteredLogs.length === 0) {
      toast.error("No data available to export");
      return;
    }
    exportToCSV(filteredLogs, `perfect-match-audit-${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Audit ledger exported successfully");
  };
  const handleClearDateRange = () => {
    setDateRange(undefined);
  };
  return (
    <AppLayout container className="bg-accent/20 scrollbar-custom">
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
              <ShieldAlert className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Audit Trail</h1>
                <Badge variant="secondary" className="animate-pulse border-border font-bold text-[10px] text-muted-foreground">
                  <Activity className="h-3 w-3 mr-1" /> LIVE
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">Immutable ledger of all system and financial actions.</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleExport} disabled={isLoading} className="rounded-xl border-border text-muted-foreground font-bold">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
        <div className="surface-panel flex flex-col gap-3 rounded-2xl p-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by actor, action or target..."
              variant="filled"
              className="h-11 rounded-xl border-border pl-10"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="rounded-xl h-11 border-border text-muted-foreground font-medium hover:bg-muted/50">
                <Calendar className="h-4 w-4 mr-2" />
                {startDate ? `${format(dateRange?.from || new Date(), 'MMM dd')} - ${dateRange?.to ? format(dateRange.to, 'MMM dd') : format(dateRange?.from || new Date(), 'MMM dd')}` : 'Date Range'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 rounded-2xl border-border" align="end">
              <DateRangePicker
                date={dateRange}
                onDateChange={setDateRange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
          <div className="overflow-x-auto scrollbar-custom">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="border-border">
                  <TableHead className="w-[180px] font-bold text-[10px] uppercase tracking-widest text-muted-foreground py-4 px-6">Timestamp</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-widest text-muted-foreground">Actor</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-widest text-muted-foreground">Action</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-widest text-muted-foreground">Target</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-widest text-muted-foreground">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i} className="border-border">
                      <TableCell className="px-6 py-4"><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                    </TableRow>
                  ))
                ) : filteredLogs.length > 0 ? (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-muted/20 transition-colors border-border">
                      <TableCell className="whitespace-nowrap font-mono text-[10px] text-muted-foreground px-6 py-4">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span className="font-bold text-sm text-foreground">{log.actorName}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[9px] font-extrabold px-2 py-0.5 rounded-md border-none",
                            log.action.includes('DELETE') && "text-destructive bg-destructive/10",
                            (log.action.includes('APPROVE') || log.action.includes('UPDATE') || log.action.includes('RECORD') || log.action.includes('CREATE')) && "text-primary bg-primary/10",
                            !log.action.includes('DELETE') && !log.action.includes('APPROVE') && !log.action.includes('UPDATE') && !log.action.includes('RECORD') && !log.action.includes('CREATE') && "text-muted-foreground bg-muted"
                          )}
                        >
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-bold text-foreground">
                        <div className="flex items-center gap-2">
                          <FileText className="h-3 w-3 text-muted-foreground" />
                          {log.target}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-medium max-w-md">
                        {log.details}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-48 text-center text-muted-foreground font-bold uppercase tracking-widest text-[10px]">
                      No security logs retrieved.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
